import React, { useState, useMemo, useEffect, useId } from 'react';
import { apiFetch } from '../api';
import McpRateLimitCell from './McpRateLimitCell';
import InfoTip from './InfoTip';
import { EmptyState, Icon, Pagination, SearchInput, Switch, TableSkeleton, Tabs } from './ui';

/**
 * Collapse catalog entries that share a `group` into a single synthetic row.
 * Tools that belong together (e.g. platform_files_list + platform_files_read)
 * are shown as ONE toggle labelled by `group_name`; toggling it enables/disables
 * every underlying tool. enabled_tools still stores the individual tool ids —
 * this is a presentation grouping only, so the runtime/SOUL/grants are unchanged.
 * The synthetic row carries `__members` (the real ids) and `__group = true`.
 */
function collapseGroups(list) {
  const out = [];
  const idx = {};
  for (const item of list) {
    if (item.group) {
      if (idx[item.group] != null) {
        out[idx[item.group]].__members.push(item.id);
        continue;
      }
      idx[item.group] = out.length;
      out.push({
        id: item.group,
        name: item.group_name || item.name,
        description: item.group_description || item.description,
        category: item.category,
        __members: [item.id],
        __group: true,
      });
    } else {
      out.push(item);
    }
  }
  return out;
}

const PAGE_SIZE = 8;

// Per-tab copy. `noun` names the list in the search box, `unit` in the pagination
// count, `column` heads the first column.
const TAB_COPY = {
  internal: {
    label: 'Internal tools',
    noun: 'internal tools',
    unit: 'tools',
    column: 'Tool',
    blurb: 'Built into the agent itself. No external service needed.',
  },
  external: {
    label: 'External tools',
    noun: 'external tools',
    unit: 'tools',
    column: 'Tool',
    blurb: 'Reach external APIs through the proxy-tools MCP server.',
  },
  rossgate: {
    label: 'RossGate',
    noun: 'RossGate services',
    unit: 'services',
    column: 'Service',
    blurb: (
      <>
        The user&apos;s own third-party accounts, reached through the RossGate gateway. A new
        user starts with the services picked under Tools, MCP, RossGate, Defaults for new
        users, and with none of the others. Each one acts on real data and costs a line in the
        agent&apos;s prompt on every turn. Enabling a service does not connect it: the user
        still has to authorise it, and the agent walks them through that.
      </>
    ),
  },
  mcp: {
    label: 'MCP servers',
    noun: 'MCP servers',
    unit: 'servers',
    column: 'Server',
    blurb: (
      <>
        Third-party Model Context Protocol servers. One toggle grants the whole server —
        every tool it publishes — so the count beside each name is how many tools the
        agent gains. The platform holds one credential per server and every user who has
        it shares that credential, its allowance and its rate limit, so these cost real
        money per call. A new user starts with the servers switched on under Tools, MCP, MCP
        servers, Defaults for new users, and with none of the others.
      </>
    ),
  },
  apps: {
    label: 'Apps and guides',
    noun: 'apps and guides',
    unit: 'apps',
    column: 'App',
    blurb: (
      <>
        Installable packages and guides. Selected apps are installed for the agent, and
        their guides are written to <code>.guides/</code>.
      </>
    ),
  },
};

// The picker sits inside the user forms. Enter in a text field submits the enclosing
// form, so without this, pressing Enter in the tools search created or saved the user.
const blockEnter = (e) => {
  if (e.key === 'Enter') e.preventDefault();
};

const ALL_CATEGORIES = ['internal', 'external', 'rossgate', 'mcp', 'apps'];

// The tabs whose tools an app can change through the service API: the only ones the User
// managed column applies to (backend/services/tool_user_managed.py). The runtime's own tools
// and the apps are not on that API at all.
const MANAGED_TABS = new Set(['external', 'rossgate', 'mcp']);

/**
 * Tools & Apps configuration with tabs:
 * - Internal Tools (built into OpenPaw)
 * - External Tools (via proxy-tools MCP)
 * - RossGate (the user's own third-party accounts)
 * - Apps & Guides (installable capabilities)
 *
 * RossGate toggles write into the SAME enabledSkills list as the other tool
 * tabs. That is not a shortcut: the admin form submits one list, and
 * `services/users.py::sync_tool_grants` picks the catalogue ids out of it and
 * projects them onto `tool_grants`, which is what proxy-tools enforces. Ids that
 * are runtime capabilities rather than catalogue tools are ignored there. So a
 * separate form field would need a second reconciler for no gain.
 */
// `userId` is passed by the edit form only: an existing user is the only one whose
// grants can carry an hourly cap, so the create form shows no cap column.
//
// `categories` picks the tabs this instance shows (the edit form splits them over three
// cards), in that order; with one left the tab strip is not drawn. `userManaged`, passed by
// the edit form only, maps each tool the User managed flag applies to onto this user's value
// and adds the column on the external, RossGate and MCP tabs; `mandatoryTools` names the
// rows it does not apply to.
function SkillsTable({ enabledSkills = [], enabledApps = [], onToggleSkill, onToggleApp, onSetAllSkills, onSetAllApps, onToggle, onSetAll, skipDefaults = false, userId = null,
                       categories = ALL_CATEGORIES, userManaged = null, mandatoryTools = null, onToggleUserManaged = null }) {
  // Backward compatibility: onToggle/onSetAll map to skill versions
  const handleToggleSkill = onToggleSkill || onToggle;
  const handleToggleApp = onToggleApp || (() => {});
  const handleSetAllSkills = onSetAllSkills || onSetAll;
  const handleSetAllApps = onSetAllApps || (() => {});
  const uid = useId();
  const [activeTab, setActiveTab] = useState(categories[0] || 'internal');
  const [search, setSearch] = useState('');
  const [internalTools, setInternalTools] = useState([]);
  const [externalTools, setExternalTools] = useState([]);
  const [rossgateTools, setRossgateTools] = useState([]);
  // Whether this environment offers RossGate at all, from GET /api/skills. An environment
  // still running the retired Maton integration does not, and there its catalogue holds no
  // RossGate rows either, so the tab would sit permanently at 0 beside a Maton app that
  // does work. Maton itself needs nothing here: it arrives through GET /api/apps like any
  // other app, so this component has no concept of it.
  //
  // Defaults TRUE so a failed fetch degrades to today's behaviour (tab present, count 0)
  // rather than making a working feature disappear on the environments that rely on it.
  const [rossgateAvailable, setRossgateAvailable] = useState(true);
  // Third-party MCP servers. No availability flag: unlike RossGate there is no
  // alternative integration to be mutually exclusive with, so an empty list just
  // means this environment defines none and the tab is hidden by its count.
  const [mcpTools, setMcpTools] = useState([]);
  // Per-server hourly caps for THIS user, keyed by server id. Fetched separately from
  // the catalogue because they belong to the user's grants, not to the server.
  const [mcpLimits, setMcpLimits] = useState(null);
  const isSuper = localStorage.getItem('admin_role') === 'superadmin';
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    Promise.all([
      apiFetch('/api/skills').then(r => r.json()),
      apiFetch('/api/apps').then(r => r.json()),
    ]).then(([skillsData, appsData]) => {
      // Parse internal tools
      const internal = Object.entries(skillsData.internal || {}).map(([id, info]) => ({
        id,
        ...info,
      }));
      setInternalTools(collapseGroups(internal));

      // Parse external tools
      const external = Object.entries(skillsData.external || {}).map(([id, info]) => ({
        id,
        ...info,
      }));
      setExternalTools(collapseGroups(external));

      // RossGate services. Not grouped: each one is a separate account the user
      // connects, so collapsing two of them behind one switch would grant access
      // to an account the admin did not tick.
      const rossgate = Object.entries(skillsData.rossgate || {}).map(([id, info]) => ({
        id,
        ...info,
      }));
      setRossgateTools(rossgate);
      // Absent on an older backend, so treat only an explicit false as "hide".
      setRossgateAvailable(skillsData.rossgate_available !== false);

      // MCP servers. Not grouped, for the same reason as RossGate: each one is a
      // separate paid credential, so collapsing two behind one switch would grant
      // one the admin did not tick.
      const mcp = Object.entries(skillsData.mcp || {}).map(([id, info]) => ({
        id,
        ...info,
      }));
      setMcpTools(mcp);

      // Parse apps (now nested under 'apps' key)
      const appsObj = appsData.apps || appsData || {};
      const appsList = Object.entries(appsObj).map(([id, info]) => ({
        id,
        ...info,
      }));
      setApps(appsList);

      // If no skills/apps are enabled yet, enable all by default (batch update)
      // Skip for edit mode (skipDefaults=true) to preserve user's choices
      if (!skipDefaults) {
        if (enabledSkills.length === 0 && skillsData.default_enabled && handleSetAllSkills) {
          // Same union as CreateUser's own seed, so whichever fetch wins they agree.
          handleSetAllSkills([
            ...skillsData.default_enabled,
            ...(skillsData.rossgate_default_enabled || []),
            ...(skillsData.mcp_default_enabled || []),
          ]);
        }
        if (enabledApps.length === 0 && appsData.default_enabled && handleSetAllApps) {
          handleSetAllApps(appsData.default_enabled);
        }
      }

      setLoading(false);
    }).catch((err) => {
      console.error('Failed to load skills/apps:', err);
      setInternalTools([]);
      setExternalTools([]);
      setRossgateTools([]);
      setMcpTools([]);
      setApps([]);
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current list based on active tab
  const currentList = useMemo(() => {
    switch (activeTab) {
      case 'internal': return internalTools;
      case 'external': return externalTools;
      case 'rossgate': return rossgateTools;
      case 'mcp': return mcpTools;
      case 'apps': return apps;
      default: return [];
    }
  }, [activeTab, internalTools, externalTools, rossgateTools, mcpTools, apps]);

  // Filter by search. A catalogue row can arrive without a description (RossGate's
  // short_desc is nullable), so every field is read defensively.
  const filtered = useMemo(() => {
    if (!search.trim()) return currentList;
    const q = search.toLowerCase();
    return currentList.filter(
      item => (item.name || '').toLowerCase().includes(q) ||
              (item.description || '').toLowerCase().includes(q) ||
              (item.category && item.category.toLowerCase().includes(q))
    );
  }, [search, currentList]);

  // Reset page when search or tab changes
  useEffect(() => { setPage(0); }, [search, activeTab]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => {
    if (!userId) return;
    apiFetch(`/api/mcp/users/${userId}/rate-limits`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => setMcpLimits(data))
      .catch(() => setMcpLimits(null));
  }, [userId]);

  const onCapSaved = (saved) => setMcpLimits(prev => prev && {
    ...prev,
    servers: prev.servers.map(s => (s.tool_id === saved.tool_id
      ? { ...s, rate_limit_per_hour: saved.rate_limit_per_hour,
          effective_rate_limit_per_hour: saved.effective_rate_limit_per_hour }
      : s)),
  });

  // Determine which enabled list and toggle function to use
  const enabledList = activeTab === 'apps' ? enabledApps : enabledSkills;
  const currentToggle = activeTab === 'apps' ? handleToggleApp : handleToggleSkill;
  const isOn = (item) => (item.__members || [item.id]).every(m => enabledList.includes(m));
  const enabledCount = currentList.filter(isOn).length;

  // Group rows fan the toggle out to every member id;
  // guard each call so we never dup or no-op a member.
  const toggleRow = (members, on) => {
    members.forEach(m => {
      const has = enabledList.includes(m);
      if (on && !has) currentToggle(m, true);
      else if (!on && has) currentToggle(m, false);
    });
  };

  // The RossGate tab is conditional; every other tab is unconditional. `activeTab` starts
  // at 'internal', so dropping this entry can never leave the component pointing at a tab
  // that is not rendered. The 'rossgate' copy in TAB_COPY is left in place deliberately:
  // it simply becomes unreachable, and deleting it would have to be undone the moment an
  // environment migrates off Maton.
  const tabs = [
    { value: 'internal', label: TAB_COPY.internal.label, count: internalTools.length },
    { value: 'external', label: TAB_COPY.external.label, count: externalTools.length },
    ...(rossgateAvailable
      ? [{ value: 'rossgate', label: TAB_COPY.rossgate.label, count: rossgateTools.length }]
      : []),
    // Hidden when the environment defines no MCP server, rather than sitting
    // permanently at 0. Unlike RossGate's flag this needs no backend signal: an
    // empty list IS the answer, because there is no alternative integration that
    // could make an empty tab meaningful.
    ...(mcpTools.length > 0
      ? [{ value: 'mcp', label: TAB_COPY.mcp.label, count: mcpTools.length }]
      : []),
    { value: 'apps', label: TAB_COPY.apps.label, count: apps.length },
  ].filter(t => categories.includes(t.value))
    .map(t => (loading ? { ...t, count: undefined } : t));

  const copy = TAB_COPY[activeTab] || TAB_COPY.internal;
  const showSize = activeTab === 'apps';
  // On an existing user's MCP tab the description gives way to the rate limit, so the
  // server name and its limit share the row. The create form keeps the description.
  const showCap = activeTab === 'mcp' && !!userId;
  const showDesc = !showCap;
  const capFor = (id) => mcpLimits?.servers.find(s => s.tool_id === id);

  // A group row is user managed only when every tool in it is, and its switch moves them
  // all. A row with no value at all is mandatory (or unknown to this environment).
  const showManaged = !!userManaged && MANAGED_TABS.has(activeTab);
  const managedOf = (members) => (members.every(m => m in userManaged)
    ? members.every(m => userManaged[m]) : null);
  const toggleManaged = (members, on) => members.forEach(m => onToggleUserManaged?.(m, on));

  return (
    <div className="stack gap-3">
      {/* Wrapped so the segmented strip keeps its own width instead of being stretched
          across the column. One category needs no strip: the card's title names it. */}
      {tabs.length > 1 && (
        <div>
          <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} label="Tool categories" />
        </div>
      )}
      <p className="text-sm text-muted">{copy.blurb}</p>

      {/* A tab panel only when there are tabs: one category is a plain region. */}
      <div className="skills-table-wrapper" role={tabs.length > 1 ? 'tabpanel' : 'region'}
           aria-label={copy.label}>
        <div className="skills-search row-wrap between">
          <SearchInput
            value={search}
            onChange={setSearch}
            onKeyDown={blockEnter}
            placeholder={`Search ${copy.noun}…`}
            label={`Search ${copy.noun}`}
            style={{ flex: '1 1 220px', maxWidth: 360 }}
          />
          {!loading && (
            <span className="text-sm text-muted num">
              {enabledCount} of {currentList.length} enabled
            </span>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={4} cols={3} label="Loading tools" />
        ) : filtered.length === 0 ? (
          search.trim() ? (
            <EmptyState
              icon="search"
              title={`No ${copy.noun} match “${search.trim()}”`}
              action={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setSearch('')}>Clear search</button>}
            >
              Search covers the name, description and category.
            </EmptyState>
          ) : (
            <EmptyState icon="tools" title={`No ${copy.noun} available`}>
              This environment&apos;s catalogue has nothing in this group yet.
            </EmptyState>
          )
        ) : (
          <div className="table-container">
            <table className="table table-compact table-stack">
              <thead>
                <tr>
                  <th scope="col" style={showCap ? { width: '50%' } : undefined}>{copy.column}</th>
                  {showDesc && <th scope="col">Description</th>}
                  {showSize && <th scope="col" className="num">Size</th>}
                  {showCap && (
                    <th scope="col" style={{ width: '50%' }}>
                      <span className="row gap-1">
                        Rate limiting
                        <InfoTip label="How rate limiting works for MCP servers">
                          The most calls this user&apos;s agent may make to the server per hour.
                          Empty keeps the server&apos;s default. Saving applies it within seconds,
                          with no redeploy; beyond it, calls that hour are refused. The provider&apos;s
                          own limits are shared by every user of the server, so a higher value takes
                          a bigger share. Switching the server off clears it.
                        </InfoTip>
                      </span>
                    </th>
                  )}
                  {showManaged && (
                    <th scope="col" style={{ width: 128, textAlign: 'center' }}>
                      <span className="row gap-1 center">
                        User managed
                        <InfoTip label="What User managed means">
                          On: apps such as RossHub list the tool and the user can switch it on
                          or off there. Off: apps do not list it, and only an admin changes its
                          Enabled switch, here. Either way the agent has it exactly when Enabled
                          is on. Mandatory tools are always on, and apps cannot change them.
                        </InfoTip>
                      </span>
                    </th>
                  )}
                  <th scope="col" style={{ width: 88, textAlign: 'center' }}>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(item => {
                  const members = item.__members || [item.id];
                  const enabled = isOn(item);
                  const switchId = `${uid}-${activeTab}-${item.id}`;
                  const keyLabel = item.key_type ? `Requires ${item.key_type} API key` : 'Requires an API key';
                  const hasBadges = item.category || item.requires_key || item.no_install
                    || item.limitation || item.supports_upload || item.supports_download
                    || item.tool_count;
                  return (
                    <tr key={item.id} className={enabled ? 'skills-row-enabled' : undefined}>
                      <td data-label={copy.column}>
                        <div className="min-w-0">
                          {/* The name labels the row's switch, so clicking it toggles too
                              and the switch is announced by the tool's name. */}
                          <label htmlFor={switchId} className="skills-name">{item.name}</label>
                          {hasBadges && (
                            <div className="skills-badges">
                              {item.category && (
                                <span className="skills-category-badge">{item.category}</span>
                              )}
                              {/* One switch, many tools. An MCP server publishes a whole
                                  suite, and without this the row reads like a single
                                  capability — so an admin cannot see that ticking it
                                  hands the agent twenty of them. */}
                              {item.tool_count > 0 && (
                                <span className="skills-category-badge"
                                      title={`Publishes ${item.tool_count} tools`}>
                                  {item.tool_count} tools
                                </span>
                              )}
                              {item.requires_key && (
                                <span className="skills-key-badge" title={keyLabel}>
                                  <Icon name="key" size={12} />
                                  <span className="sr-only">{keyLabel}</span>
                                </span>
                              )}
                              {item.no_install && (
                                <span className="skills-guide-badge" title="Guide only, no installation">
                                  <Icon name="fileText" size={12} />
                                  <span className="sr-only">Guide only, no installation</span>
                                </span>
                              )}
                              {/* A service whose OAuth grant cannot do what its reference
                                  describes. Shown HERE because this is the moment someone
                                  decides to enable it, finding out from a user's bug
                                  report instead is the outcome worth preventing. */}
                              {item.limitation && (
                                <span className="row gap-1">
                                  <span className="skills-warn-badge">
                                    <Icon name="alert" size={12} />
                                    limited
                                  </span>
                                  <InfoTip label={`What ${item.name} cannot do`}>{item.limitation}</InfoTip>
                                </span>
                              )}
                              {(item.supports_upload || item.supports_download) && (
                                <span
                                  className="skills-category-badge"
                                  title="Can move files to or from this service"
                                >
                                  files
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      {showDesc && (
                        <td data-label="Description" className="skills-desc-cell">{item.description}</td>
                      )}
                      {showSize && (
                        <td data-label="Size" className="num skills-size-cell">
                          {item.size_mb > 0 ? `${item.size_mb} MB` : '—'}
                        </td>
                      )}
                      {showCap && (
                        <td data-label="Rate limiting">
                          {/* Every row has its cap: the rows and /rate-limits both come
                              from the servers this environment enables. The dash is left
                              for a failed or older answer only. */}
                          {!mcpLimits ? (
                            <span className="text-sm text-muted">…</span>
                          ) : capFor(item.id) ? (
                            <McpRateLimitCell
                              userId={userId}
                              server={capFor(item.id)}
                              maxRate={mcpLimits.max_rate_limit_per_hour}
                              canEdit={isSuper && mcpLimits.enabled}
                              onSaved={onCapSaved}
                              label={`Rate limit for ${item.name}, in calls per hour`}
                            />
                          ) : <span className="text-sm text-muted">—</span>}
                        </td>
                      )}
                      {showManaged && (
                        <td data-label="User managed" className="text-center">
                          {managedOf(members) !== null ? (
                            <Switch
                              id={`${switchId}-managed`}
                              checked={managedOf(members)}
                              onChange={on => toggleManaged(members, on)}
                              aria-label={`${item.name}: user managed`}
                            />
                          ) : (mandatoryTools || []).some(m => members.includes(m)) ? (
                            <span className="text-sm text-muted">Mandatory</span>
                          ) : <span className="text-sm text-muted">-</span>}
                        </td>
                      )}
                      <td data-label="Enabled" className="text-center">
                        <Switch id={switchId} checked={enabled} onChange={on => toggleRow(members, on)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > PAGE_SIZE && (
          <div style={{ padding: '0 var(--sp-3) var(--sp-2)' }}>
            <Pagination
              page={page + 1}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPage={p => setPage(p - 1)}
              noun={copy.unit}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SkillsTable;
